/**
 * `word_artifacts` from a Word turn's poll payload (`app/scholar/agent/word_tools.py` `WordTurn.artifacts()`):
 *
 *   {snapshot_id, doc_hash, edits, comments, inserts, findings, compares, truncated, error?}
 *
 * Each item carries `paragraph_hash` (the snapshot's hash of its pid; `paragraph_hashes` for items over several
 * pids), so a card can re-anchor even when the pane no longer holds the snapshot it was verified against.
 *
 * The payload is untrusted data: every item is re-checked here and a malformed one is dropped, never
 * guessed at. Strings stay strings — the UI renders them as text nodes only.
 */

import { PID_PATTERN, type WordSnapshot } from "../anchor/snapshot";

export type EditOp = "replace" | "delete" | "insert_after";
export type InsertAt = "cursor" | "after_pid" | "replace_selection";

export interface EditArtifact {
  edit_id: string;
  pid: string;
  quote: string;
  /** The snapshot's own characters at the verified span (in `basis` text). */
  matched_text: string;
  replacement: string;
  op: EditOp;
  why: string;
  level: string;
  /** "live" = Paragraph.text; "reviewed" = as if pending changes were accepted. */
  basis: string;
  warnings: string[];
  /** Hash of the pid's paragraph in the snapshot Ella read ("" if the server sent none). */
  paragraph_hash: string;
}

export interface CommentArtifact {
  comment_id: string;
  pid: string;
  quote: string;
  matched_text: string;
  text: string;
  level: string;
  basis: string;
  warnings: string[];
  paragraph_hash: string;
}

/** A citation in drafted text and what the cite-check made of it (e.g. "found in …", "not checked"). */
export interface InsertCitation {
  text: string;
  status: string;
}

export interface InsertArtifact {
  insert_id: string;
  at: InsertAt;
  /** Empty unless `at` is "after_pid". */
  pid: string;
  markdown: string;
  warnings: string[];
  paragraph_hash: string;
  /** Citations in the draft with their check status (older servers send none). */
  citations: InsertCitation[];
}

export type CitationTone = "ok" | "caution" | "bad";

/**
 * How a citation status reads: `ok` only when the cite was found (and nothing in it differs); `bad` when it
 * was not found or its quote/pin differs; anything else — not checked, could not check, outside coverage, or
 * a status this pane does not know — is a caution, never silently fine.
 */
export function citationTone(status: string): CitationTone {
  const s = status.trim().toLowerCase();
  if (/^not found\b|^not_found\b|\bdiffers\b/.test(s)) return "bad";
  if (/^(found|verified|ok)\b/.test(s)) return "ok";
  return "caution";
}

/** A draft whose citations are not all confirmed is inserted only on purpose ("Insert anyway"). */
export function hasUnconfirmedCitation(insert: Pick<InsertArtifact, "citations">): boolean {
  return insert.citations.some((c) => citationTone(c.status) !== "ok");
}

export interface FindingArtifact {
  finding_id: string;
  check: string;
  kind: string;
  pids: string[];
  message: string;
  evidence: string[];
}

export type CompareKind = "modified" | "added" | "removed" | "moved";

/** One difference: the open document (`open_*`, its pids) against the other side (`other_*`). */
export interface CompareChange {
  change_id: string;
  kind: CompareKind;
  /** First line of the clause, for orientation. */
  clause: string;
  /** Open-document paragraphs (empty for a removed clause). */
  pids: string[];
  paragraph_hashes: string[];
  open_text: string;
  other_text: string;
  /** Where it sits in the other side: "¶p3" in a saved version, "p.2" in a PDF; "" when added. */
  other_where: string;
  materiality: "high" | "medium" | "low";
  why: string;
}

export interface CompareArtifact {
  compare_id: string;
  against: { kind: "version" | "document"; id: string; name: string };
  changes: CompareChange[];
  /** All differences found (more than `changes` when truncated). */
  total: number;
  truncated: boolean;
  /** The comparison hit its time limit; later clauses were not compared. */
  partial: boolean;
}

export interface WordArtifacts {
  snapshot_id: string;
  doc_hash: string;
  edits: EditArtifact[];
  comments: CommentArtifact[];
  inserts: InsertArtifact[];
  findings: FindingArtifact[];
  compares: CompareArtifact[];
  truncated: boolean;
  /** Set when the server could not load the pane's snapshot this turn (e.g. "missing", "too_large"). */
  error?: string;
}

/** Server warning on an edit/insert whose target text has a pending tracked change (advisory, spec §3 [1.2]). */
export const PENDING_CHANGE_WARNING = "pending_change_warning";

const EDIT_OPS: ReadonlySet<string> = new Set(["replace", "delete", "insert_after"]);
const COMPARE_KINDS: ReadonlySet<string> = new Set(["modified", "added", "removed", "moved"]);
const MATERIALITY: ReadonlySet<string> = new Set(["high", "medium", "low"]);
const HASH16 = /^[0-9a-f]{16}$/;
const INSERT_ATS: ReadonlySet<string> = new Set(["cursor", "after_pid", "replace_selection"]);

type Obj = Record<string, unknown>;

function isObj(value: unknown): value is Obj {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function items<T>(value: unknown, parse: (raw: Obj) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  const out: T[] = [];
  for (const raw of value) {
    if (!isObj(raw)) continue;
    const parsed = parse(raw);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function validPid(pid: string): boolean {
  return PID_PATTERN.test(pid);
}

function hash16(value: unknown): string {
  const h = str(value);
  return HASH16.test(h) ? h : "";
}

function parseEdit(raw: Obj): EditArtifact | null {
  const edit: EditArtifact = {
    edit_id: str(raw.edit_id),
    pid: str(raw.pid),
    quote: str(raw.quote),
    matched_text: str(raw.matched_text),
    replacement: str(raw.replacement),
    op: str(raw.op) as EditOp,
    why: str(raw.why),
    level: str(raw.level),
    basis: str(raw.basis),
    warnings: strings(raw.warnings),
    paragraph_hash: hash16(raw.paragraph_hash),
  };
  if (!edit.edit_id || !validPid(edit.pid) || !edit.quote || !EDIT_OPS.has(edit.op)) return null;
  if (edit.op !== "delete" && !edit.replacement) return null;
  return edit;
}

function parseComment(raw: Obj): CommentArtifact | null {
  const comment: CommentArtifact = {
    comment_id: str(raw.comment_id),
    pid: str(raw.pid),
    quote: str(raw.quote),
    matched_text: str(raw.matched_text),
    text: str(raw.text),
    level: str(raw.level),
    basis: str(raw.basis),
    warnings: strings(raw.warnings),
    paragraph_hash: hash16(raw.paragraph_hash),
  };
  return comment.comment_id && validPid(comment.pid) && comment.quote && comment.text.trim() ? comment : null;
}

function parseInsertCitation(raw: Obj): InsertCitation | null {
  const text = str(raw.text).trim();
  if (!text) return null;
  // A cite with no status was not checked: say so rather than show it as fine.
  return { text, status: str(raw.status).trim() || "not checked" };
}

function parseInsert(raw: Obj): InsertArtifact | null {
  const insert: InsertArtifact = {
    insert_id: str(raw.insert_id),
    at: str(raw.at) as InsertAt,
    pid: str(raw.pid),
    markdown: str(raw.markdown),
    warnings: strings(raw.warnings),
    paragraph_hash: hash16(raw.paragraph_hash),
    citations: items(raw.citations, parseInsertCitation),
  };
  if (!insert.insert_id || !INSERT_ATS.has(insert.at) || !insert.markdown.trim()) return null;
  if (insert.at === "after_pid" && !validPid(insert.pid)) return null;
  return insert;
}

function parseFinding(raw: Obj): FindingArtifact | null {
  const finding: FindingArtifact = {
    finding_id: str(raw.finding_id),
    check: str(raw.check),
    kind: str(raw.kind),
    pids: strings(raw.pids).filter(validPid),
    message: str(raw.message),
    evidence: strings(raw.evidence),
  };
  return finding.finding_id && finding.message ? finding : null;
}

function parseCompareChange(raw: Obj): CompareChange | null {
  const pids: string[] = [];
  const hashes: string[] = [];
  const rawHashes = Array.isArray(raw.paragraph_hashes) ? raw.paragraph_hashes : [];
  if (Array.isArray(raw.pids)) {
    raw.pids.forEach((pid, i) => {
      if (typeof pid === "string" && validPid(pid)) {
        pids.push(pid);
        hashes.push(hash16(rawHashes[i]));
      }
    });
  }
  const change: CompareChange = {
    change_id: str(raw.change_id),
    kind: str(raw.kind) as CompareKind,
    clause: str(raw.clause),
    pids,
    paragraph_hashes: hashes,
    open_text: str(raw.open_text),
    other_text: str(raw.other_text),
    other_where: str(raw.other_where),
    materiality: (MATERIALITY.has(str(raw.materiality)) ? str(raw.materiality) : "low") as CompareChange["materiality"],
    why: str(raw.why),
  };
  if (!change.change_id || !COMPARE_KINDS.has(change.kind)) return null;
  return change.open_text || change.other_text ? change : null;
}

function parseCompare(raw: Obj): CompareArtifact | null {
  const against = isObj(raw.against) ? raw.against : {};
  const kind = str(against.kind);
  if (!str(raw.compare_id) || (kind !== "version" && kind !== "document")) return null;
  const changes = items(raw.changes, parseCompareChange);
  return {
    compare_id: str(raw.compare_id),
    against: { kind, id: str(against.id), name: str(against.name) || (kind === "version" ? "saved version" : "document") },
    changes,
    total: typeof raw.total === "number" && Number.isFinite(raw.total) ? Math.max(raw.total, changes.length) : changes.length,
    truncated: raw.truncated === true,
    partial: raw.partial === true,
  };
}

export function parseWordArtifacts(raw: unknown): WordArtifacts | null {
  if (!isObj(raw)) return null;
  const artifacts: WordArtifacts = {
    snapshot_id: str(raw.snapshot_id),
    doc_hash: str(raw.doc_hash),
    edits: items(raw.edits, parseEdit),
    comments: items(raw.comments, parseComment),
    inserts: items(raw.inserts, parseInsert),
    findings: items(raw.findings, parseFinding),
    compares: items(raw.compares, parseCompare),
    truncated: raw.truncated === true,
  };
  const error = str(raw.error);
  if (error) artifacts.error = error;
  return artifacts;
}

export function isEmptyArtifacts(a: WordArtifacts): boolean {
  return a.edits.length + a.comments.length + a.inserts.length + a.findings.length + a.compares.length === 0;
}

// ---- document order -----------------------------------------------------------------------------------------

const STORY_RANK: Record<string, number> = { p: 0, fn: 1, en: 2, hf: 3 };

/** Sort key for a pid without a snapshot: body, then footnotes, endnotes, headers/footers; numeric within. */
export function pidSortKey(pid: string): [number, number, number] {
  const m = /^(p|fn|en|hf)(\d+)(?:\.(\d+))?$/.exec(pid);
  if (m === null) return [9, 0, 0];
  return [STORY_RANK[m[1] as string] ?? 9, Number(m[2]), Number(m[3] ?? 0)];
}

function comparePids(a: string, b: string, index: ReadonlyMap<string, number>): number {
  const ia = index.get(a);
  const ib = index.get(b);
  if (ia !== undefined && ib !== undefined) return ia - ib;
  const ka = pidSortKey(a);
  const kb = pidSortKey(b);
  return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
}

function idNumber(id: string): number {
  const m = /(\d+)$/.exec(id);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

export type CardRef =
  | { kind: "edit"; id: string; pid: string; item: EditArtifact }
  | { kind: "comment"; id: string; pid: string; item: CommentArtifact }
  | { kind: "insert"; id: string; pid: string; item: InsertArtifact };

export interface ParagraphGroup {
  pid: string;
  /** Word's list number for the paragraph ("3.2", "(a)") when the snapshot has one. */
  listString: string | null;
  /** Start of the paragraph's text, for orientation. */
  preview: string;
  cards: CardRef[];
}

export interface GroupedArtifacts {
  /** Inserts at the cursor / replacing the selection: not tied to a paragraph. */
  loose: CardRef[];
  groups: ParagraphGroup[];
}

const PREVIEW_CHARS = 90;

function preview(text: string): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length <= PREVIEW_CHARS ? flat : `${flat.slice(0, PREVIEW_CHARS - 1).trimEnd()}…`;
}

/**
 * Edits, comments and after-¶ inserts grouped by paragraph, paragraphs in document order (the snapshot's
 * order when known). Within a paragraph: by where the quoted text starts, then edits before comments,
 * then the id's number; after-¶ inserts come last in their paragraph (they land after it).
 */
export function groupByParagraph(artifacts: WordArtifacts, snapshot: WordSnapshot | null): GroupedArtifacts {
  const index = new Map<string, number>();
  const texts = new Map<string, { live: string; reviewed: string; list: string | null }>();
  snapshot?.paragraphs.forEach((p, i) => {
    index.set(p.pid, i);
    texts.set(p.pid, { live: p.text_live, reviewed: p.text_reviewed, list: p.list_string ?? null });
  });

  const position = (pid: string, basis: string, matched: string, quote: string): number => {
    const t = texts.get(pid);
    if (t === undefined) return Number.MAX_SAFE_INTEGER;
    const text = basis === "reviewed" ? t.reviewed : t.live;
    for (const needle of [matched, quote]) {
      if (!needle) continue;
      const at = text.indexOf(needle);
      if (at !== -1) return at;
    }
    return Number.MAX_SAFE_INTEGER;
  };

  const kindRank = { edit: 0, comment: 1, insert: 2 } as const;
  const loose: CardRef[] = [];
  const byPid = new Map<string, { card: CardRef; pos: number }[]>();
  const add = (card: CardRef, pos: number): void => {
    const list = byPid.get(card.pid) ?? [];
    list.push({ card, pos });
    byPid.set(card.pid, list);
  };
  for (const e of artifacts.edits) add({ kind: "edit", id: e.edit_id, pid: e.pid, item: e }, position(e.pid, e.basis, e.matched_text, e.quote));
  for (const c of artifacts.comments) {
    add({ kind: "comment", id: c.comment_id, pid: c.pid, item: c }, position(c.pid, c.basis, c.matched_text, c.quote));
  }
  for (const i of artifacts.inserts) {
    const card: CardRef = { kind: "insert", id: i.insert_id, pid: i.pid, item: i };
    if (i.at === "after_pid") add(card, Number.MAX_SAFE_INTEGER);
    else loose.push(card);
  }

  const groups: ParagraphGroup[] = [...byPid.keys()]
    .sort((a, b) => comparePids(a, b, index))
    .map((pid) => {
      const cards = (byPid.get(pid) ?? [])
        .sort((a, b) => a.pos - b.pos || kindRank[a.card.kind] - kindRank[b.card.kind] || idNumber(a.card.id) - idNumber(b.card.id))
        .map((entry) => entry.card);
      const t = texts.get(pid);
      return { pid, listString: t?.list ?? null, preview: t ? preview(t.live) : "", cards };
    });
  loose.sort((a, b) => idNumber(a.id) - idNumber(b.id));
  return { loose, groups };
}

/** Edits in the order "Apply all" walks them: document order, as shown. */
export function editsInDocumentOrder(artifacts: WordArtifacts, snapshot: WordSnapshot | null): EditArtifact[] {
  return groupByParagraph(artifacts, snapshot).groups.flatMap((g) =>
    g.cards.flatMap((c) => (c.kind === "edit" ? [c.item] : [])),
  );
}

/** Short label for a pid: "¶12" for body paragraph p12, "footnote 1" for fn1.x, "header/footer" for hf. */
export function pidLabel(pid: string): string {
  const m = /^(p|fn|en|hf)(\d+)(?:\.(\d+))?$/.exec(pid);
  if (m === null) return `¶${pid}`;
  if (m[1] === "p") return `¶${m[2]}`;
  if (m[1] === "fn") return `footnote ${m[2]}`;
  if (m[1] === "en") return `endnote ${m[2]}`;
  return "header/footer";
}

/** Authors of pending tracked changes captured in `pid` (for the pending-change warning). */
export function changeAuthors(snapshot: WordSnapshot | null, pid: string): string[] {
  if (snapshot === null) return [];
  const authors = new Set<string>();
  for (const change of snapshot.tracked_changes) {
    if (change.pid === pid && change.type !== "formatted" && change.author.trim()) authors.add(change.author.trim());
  }
  return [...authors];
}

export const CHECK_LABELS: Readonly<Record<string, string>> = {
  defined_terms: "Defined terms",
  cross_references: "Cross-references",
  numbering: "Numbering",
  party_names: "Party names",
  dates: "Dates",
  amounts: "Amounts",
  placeholders: "Placeholders",
};
